import { IconCheck, IconShield, IconBolt } from './icons.jsx';
import { url } from './url.js';

// ─────────────────────────────────────────────────────────────────────
// Rotterdam-specific deep content — only rendered when stad.slug === "rotterdam".
// Includes: long-form SEO context, wijk-grid with primary pest per neighborhood,
// expanded service descriptions matching the master content brief.
// ─────────────────────────────────────────────────────────────────────

const ROTTERDAM_WIJKEN = [
  { name: "Centrum / Oude Haven",      tag: "Kakkerlakken & Bedwantsen", desc: "Horeca en toerisme dragen bij aan kakkerlakken in keukens en bedwantsen in hotels en Airbnb-panden." },
  { name: "Oude Noorden / Crooswijk",  tag: "Ratten & Muizen",           desc: "Zwaarst getroffen wijken. Gedateerde riolering en aaneengesloten bebouwing zorgen voor aanhoudende overlast." },
  { name: "Mathenesse / Delfshaven",   tag: "Ratten",                    desc: "Nabijheid van het Noorderkanaal en water creëert ideale rattenbiotoop in tuinen, kelders en vloeren." },
  { name: "Feijenoord / IJsselmonde",  tag: "Muizen",                    desc: "Gedeelde kruipruimtes in sociale huurwoningen zorgen voor snelle verspreiding tussen appartementen." },
  { name: "Hillegersberg / Overschie", tag: "Wespen & Mollen",           desc: "Groene omgeving en vrijstaande woningen: meer wespennesten in spouwmuren en mollen in tuinen." },
  { name: "Kralingen",                 tag: "Wespen & Ratten",           desc: "Nabijheid Kralingse Plas, ratten in tuinen, wespen in bomen en schuren tijdens het hele seizoen." },
  { name: "Charlois / Zuidplein",      tag: "Muizen",                    desc: "Appartementen met muizenoverlast via spouwmuren en gedeelde kruipruimtes. Hoge dichtheid." },
  { name: "Pernis / Botlek / Europoort", tag: "Ratten",                  desc: "Industrieel havengebied, ratten reizen mee via internationale scheepvaart. Bedrijven primaire doelgroep." },
];

function RotterdamContext({ stad }) {
  return (
    <section className="section section-bg">
      <div className="container">
        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 56, alignItems: 'flex-start' }} className="rdam-ctx-grid">
          <div>
            <div className="section-tag">Rotterdam-context</div>
            <h2 style={{ fontSize: 'clamp(1.7rem,3vw,2.4rem)', fontWeight: 800, color: 'var(--navy)', margin: '10px 0 18px', lineHeight: 1.2 }}>
              Rotterdam: <em style={{ color: 'var(--orange)', fontStyle: 'normal' }}>koploper</em> in ongedierteoverlast
            </h2>
            <p style={{ color: 'var(--text)', fontSize: '1rem', lineHeight: 1.75, marginBottom: 16 }}>
              Rotterdam staat bovenaan de nationale lijst van steden met de meeste ongediertemeldingen. Als grootste
              havenstad van Europa trekt de stad ongedierte aan via internationale scheepvaart, de drukke horeca rondom de
              Markthal en Oude Haven, en de hoge omloopsnelheid van woningen door studenten, toeristen en expats.
            </p>
            <p style={{ color: 'var(--text)', fontSize: '1rem', lineHeight: 1.75, marginBottom: 22 }}>
              Wijken als het <strong>Oude Noorden, Crooswijk en Mathenesse</strong> kennen structurele ratten- en muizenoverlast.
              In <strong>Feijenoord en IJsselmonde</strong> verspreiden muizen zich via gedeelde kruipruimtes. <strong>Hillegersberg
              en Kralingen</strong> hebben meer wespen- en mollenproblemen. De <strong>Havengebieden</strong> kennen rattenproblemen
              door internationale goederenstromen.
            </p>
            <ul className="checklist">
              <li><IconCheck /> Dagelijks actief in alle Rotterdam-wijken</li>
              <li><IconCheck /> Gemiddeld binnen 24 uur op locatie</li>
              <li><IconCheck /> Werkt met gecertificeerde partners</li>
              <li><IconCheck /> Veilig voor kinderen en huisdieren</li>
              <li><IconCheck /> Vakkundige nacontrole na behandeling</li>
              <li><IconCheck /> Mechanische én chemische methoden</li>
              <li><IconCheck /> Ook voor bedrijven, horeca en zorginstellingen</li>
              <li><IconCheck /> Preventiecontracten voor structurele bescherming</li>
            </ul>
          </div>
          {/* Vaste hoogte 480px + object-fit:cover + object-position
              die de bestrijder (rechts in beeld) altijd vol toont, ook bij
              crop op smalle viewports. De bomen-achtergrond links wordt
              dan iets afgesneden — dat is OK want bestrijder is wat telt. */}
          <div className="seo-photo seo-photo--filled" style={{ height: 480 }}>
            <picture>
              <source srcSet="/assets/photo-inspecteur-rotterdam.webp" type="image/webp" />
              <img
                src="/assets/photo-inspecteur-rotterdam.jpg"
                alt={`ABC bestrijder in beschermende kleding tijdens ongediertebestrijding in ${stad.naam}`}
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'right center', display: 'block' }}
              />
            </picture>
          </div>
        </div>
        <style>{`@media (max-width:880px){ .rdam-ctx-grid{ grid-template-columns:1fr !important; } }`}</style>
      </div>
    </section>
  );
}

function RotterdamWijken({ stad }) {
  return (
    <section className="section">
      <div className="container">
        <div className="sh">
          <div className="section-tag">Per wijk in Rotterdam</div>
          <h2>Ongedierteoverlast per <em>wijk</em></h2>
          <p>Niet elke wijk heeft hetzelfde probleem. Wij kennen Rotterdam van binnen en van buiten.</p>
        </div>
        <div className="wijk-grid">
          {ROTTERDAM_WIJKEN.map(w => (
            <div key={w.name} className="wijk-card">
              <h3>{w.name}</h3>
              <span className="wijk-tag">{w.tag}</span>
              <p>{w.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function RotterdamExtras({ stad }) {
  if (stad.slug !== "rotterdam") return null;
  // Wijken grid is now handled by RotterdamWijkenLuxe (premium variant)
  // rendered directly from stad.jsx, so we only show the context block here.
  return (
    <>
      <RotterdamContext stad={stad} />
    </>
  );
}

